// Iter-228y — Marge Réelle Cabinet (Real Company Margin)
//
// Shows the CEO the actual company P&L per month:
//   revenue (règlements encaissés) − dépenses (NDF+Cabinet) − salaires (coût employeur)
//   = net réel + margin % (top KPI card).
// Includes a 12-month rolling curve + a per-mission salary allocation table.
//
// Data sources:
//   • GET /admin/real-margin?year=Y&month=M            — single-month KPIs
//   • GET /admin/real-margin/timeline?months=12        — rolling KPIs
//   • GET /admin/real-margin/by-mission?year=Y&month=M — CRA-weighted salary alloc.
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Wallet, Building2, Users, Loader2, FileDown, Mail, Settings as SettingsIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const MONTH_LABELS_FR = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
  "Juil", "Août", "Sep", "Oct", "Nov", "Déc",
];

const fmtMad = (n) => (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);

function KpiCard({ label, value, sub, tone = "zinc", testid, icon: Icon }) {
  const tones = {
    zinc: "border-zinc-200 bg-white",
    emerald: "border-emerald-200 bg-emerald-50/40",
    rose: "border-rose-200 bg-rose-50/40",
    sky: "border-sky-200 bg-sky-50/40",
    amber: "border-amber-200 bg-amber-50/40",
  };
  return (
    <div className={`rounded-lg border ${tones[tone]} px-4 py-3`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase font-mono tracking-wider text-zinc-500">
        {Icon && <Icon size={12} />}
        <span>{label}</span>
      </div>
      <div className="text-2xl font-semibold text-zinc-900 font-mono mt-1">{value}</div>
      {sub && <div className="text-[10px] font-mono text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function RealMarginPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [kpi, setKpi] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [byMission, setByMission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailForm, setEmailForm] = useState({
    real_margin_email_enabled: false,
    real_margin_email_to: "",
    real_margin_email_cc: "",
    real_margin_email_subject_template: "",
    real_margin_email_body_template: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [k, t, m] = await Promise.all([
        api.get(`/admin/real-margin?year=${year}&month=${month}`),
        api.get(`/admin/real-margin/timeline?months=12`),
        api.get(`/admin/real-margin/by-mission?year=${year}&month=${month}`),
      ]);
      setKpi(k.data);
      setTimeline(t.data?.months || []);
      setByMission(m.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du chargement");
    } finally {
      setLoading(false);
    }
  };

  const loadEmailCfg = async () => {
    try {
      const r = await api.get("/admin/real-margin/email-config");
      setEmailCfg(r.data);
      setEmailForm({
        real_margin_email_enabled: !!r.data?.enabled,
        real_margin_email_to: r.data?.to || "",
        real_margin_email_cc: r.data?.cc || "",
        real_margin_email_subject_template: r.data?.subject_template || "",
        real_margin_email_body_template: r.data?.body_template || "",
      });
    } catch (e) { /* silent */ }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/admin/real-margin/export.pdf?year=${year}&month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `pnl_cabinet_${year}-${String(month).padStart(2, "0")}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'export PDF");
    } finally {
      setDownloading(false);
    }
  };

  const sendEmailNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/real-margin/send-email", { year, month });
      toast.success(`P&L envoyé à ${(r.data?.to || []).join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const saveEmailCfg = async () => {
    try {
      await api.put("/settings", emailForm);
      toast.success("Configuration enregistrée");
      await loadEmailCfg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    }
  };

  useEffect(() => { load(); }, [year, month]);
  useEffect(() => { loadEmailCfg(); }, []);

  // Max abs value from timeline for chart scaling.
  const chartMax = useMemo(() => {
    const vals = timeline.flatMap((r) => [r.revenue_mad || 0, r.cash_out_mad || 0, Math.abs(r.net_mad || 0)]);
    return Math.max(1, ...vals);
  }, [timeline]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2" data-testid="real-margin-heading">
            <TrendingUp size={22} /> Marge Réelle Cabinet
          </h1>
          <div className="text-sm text-zinc-500 mt-1">
            Vue P&amp;L : règlements encaissés − dépenses cabinet − salaires (coût employeur).
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border border-zinc-300 rounded px-2 py-1 text-sm" data-testid="real-margin-year-select">
            {[today.getFullYear() - 2, today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="border border-zinc-300 rounded px-2 py-1 text-sm" data-testid="real-margin-month-select">
            {MONTH_LABELS_FR.map((l, i) => <option key={i} value={i + 1}>{l}</option>)}
          </select>
          <button
            type="button" onClick={downloadPdf} disabled={downloading}
            data-testid="real-margin-download-pdf-btn"
            className="inline-flex items-center gap-1.5 border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 rounded px-2 py-1 text-xs font-medium disabled:opacity-60"
          >
            {downloading ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />}
            Télécharger PDF
          </button>
          <button
            type="button" onClick={sendEmailNow} disabled={sending || !emailCfg?.to}
            title={emailCfg?.to ? `Envoyer à ${emailCfg.to}` : "Configurez d'abord un destinataire dans les paramètres"}
            data-testid="real-margin-send-email-btn"
            className="inline-flex items-center gap-1.5 border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 rounded px-2 py-1 text-xs font-medium disabled:opacity-60"
          >
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
            Envoyer par email
          </button>
          <button
            type="button" onClick={() => setShowSettings(true)}
            data-testid="real-margin-settings-btn"
            className="inline-flex items-center gap-1.5 border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 rounded px-2 py-1 text-xs font-medium"
          >
            <SettingsIcon size={13} /> Auto-email mensuel
          </button>
          {loading && <Loader2 size={14} className="animate-spin text-zinc-500" />}
        </div>
      </div>

      {/* Top KPIs */}
      {kpi && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard label="Règlements encaissés" value={`${fmtMad(kpi.revenue_mad)} MAD`} sub={`${kpi.n_payments} règlement(s)`} tone="emerald" icon={Wallet} testid="rm-kpi-revenue" />
          <KpiCard label="Dépenses cabinet" value={`${fmtMad(kpi.expenses_mad)} MAD`} sub={`${kpi.n_expenses} ligne(s)`} tone="rose" icon={Building2} testid="rm-kpi-expenses" />
          <KpiCard label="Salaires (coût employeur)" value={`${fmtMad(kpi.salaries_mad)} MAD`} sub={`${kpi.n_salaries} salarié(s)`} tone="rose" icon={Users} testid="rm-kpi-salaries" />
          <KpiCard
            label="Marge nette réelle"
            value={`${fmtMad(kpi.net_mad)} MAD`}
            sub={`${fmtPct(kpi.margin_pct)} du CA encaissé`}
            tone={kpi.net_mad >= 0 ? "emerald" : "rose"}
            icon={kpi.net_mad >= 0 ? TrendingUp : TrendingDown}
            testid="rm-kpi-net"
          />
        </div>
      )}

      {/* Iter-246 — excluded employees diagnostic */}
      {kpi && (kpi.salaries_skipped || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 space-y-1" data-testid="rm-salaries-skipped-banner">
          <div className="text-xs font-semibold text-amber-900">
            ⚠ {kpi.salaries_skipped.length} salarié(s) exclu(s) du coût employeur ce mois :
          </div>
          {kpi.salaries_skipped.map((s, i) => (
            <div key={i} className="text-[11px] text-amber-800 font-mono">{s.user_name} — {s.reason}</div>
          ))}
          <div className="text-[10px] text-amber-700">Corrigez via Admin › Utilisateurs › Rémunération.</div>
        </div>
      )}

      {/* 12-month timeline (mini bar-chart, SVG-free for zero-dep). */}
      {timeline.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="real-margin-timeline">
          <h2 className="text-sm font-semibold text-zinc-900 mb-3">Évolution 12 mois glissants</h2>
          <div className="grid grid-cols-12 gap-1 h-40 items-end">
            {timeline.map((r) => {
              const rev = (r.revenue_mad || 0) / chartMax;
              const co = (r.cash_out_mad || 0) / chartMax;
              const netAbs = Math.abs(r.net_mad || 0) / chartMax;
              const negative = (r.net_mad || 0) < 0;
              const isCurrent = r.year === year && r.month === month;
              return (
                <div key={r.period_label} className={`flex flex-col justify-end h-full ${isCurrent ? "bg-sky-50/40" : ""}`}
                     data-testid={`timeline-col-${r.year}-${r.month}`}>
                  <div className="flex items-end justify-center gap-0.5 h-full">
                    <div className="w-1.5 bg-emerald-500/70 rounded-sm" style={{ height: `${rev * 100}%` }} title={`Règlements: ${fmtMad(r.revenue_mad)}`} />
                    <div className="w-1.5 bg-rose-500/70 rounded-sm" style={{ height: `${co * 100}%` }} title={`Cash-out: ${fmtMad(r.cash_out_mad)}`} />
                    <div className={`w-1.5 rounded-sm ${negative ? "bg-rose-800" : "bg-emerald-800"}`} style={{ height: `${netAbs * 100}%` }} title={`Net: ${fmtMad(r.net_mad)}`} />
                  </div>
                  <div className="text-[9px] font-mono text-zinc-500 text-center mt-1">{MONTH_LABELS_FR[r.month - 1]}<br /><span className="text-zinc-400">{String(r.year).slice(2)}</span></div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 text-[10px] font-mono text-zinc-500 mt-2">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500/70 rounded-sm" /> Règlements</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-rose-500/70 rounded-sm" /> Cash-out (dépenses + salaires)</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-800 rounded-sm" /> Net positif</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-rose-800 rounded-sm" /> Net négatif</span>
          </div>
        </div>
      )}

      {/* Salary allocation per mission */}
      {byMission && (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="real-margin-by-mission">
          <div className="px-4 py-2.5 bg-zinc-50/60 border-b border-zinc-100 flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-sm font-semibold text-zinc-900">Allocation salaires par mission (prorata CRA)</h2>
              <div className="text-[10px] uppercase font-mono tracking-wider text-zinc-500 mt-0.5">
                Répartition du coût employeur au prorata des heures imputées sur chaque mission.
              </div>
            </div>
            <span className="text-xs font-mono text-zinc-500">Total : {fmtMad(byMission.total_mad)} MAD</span>
          </div>
          {(byMission.missions || []).length === 0 && byMission.unassigned_mad === 0 && (
            <div className="px-4 py-6 text-sm text-zinc-400 text-center">Aucune allocation ce mois.</div>
          )}
          {((byMission.missions || []).length > 0 || byMission.unassigned_mad > 0) && (
            <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-zinc-500 font-mono uppercase tracking-wider text-[10px] border-b border-zinc-100">
                  <th className="text-left px-3 py-1.5">Mission</th>
                  <th className="text-left px-3 py-1.5">Devise</th>
                  <th className="text-right px-3 py-1.5">Salaire alloué (MAD)</th>
                  <th className="text-right px-3 py-1.5">% du total salaires</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {(byMission.missions || []).map((m) => {
                  const pct = byMission.total_mad > 0 ? (m.allocated_salary_mad / byMission.total_mad * 100) : 0;
                  return (
                    <tr key={m.mission_id} className="hover:bg-zinc-50/40" data-testid={`mission-alloc-${m.mission_id}`}>
                      <td className="px-3 py-1.5 truncate max-w-md" title={m.mission_title}>{m.mission_title}</td>
                      <td className="px-3 py-1.5 font-mono text-zinc-500">{m.currency}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtMad(m.allocated_salary_mad)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-zinc-600">{pct.toFixed(1)}%</td>
                    </tr>
                  );
                })}
                {byMission.unassigned_mad > 0 && (
                  <tr className="bg-amber-50/40" data-testid="mission-alloc-unassigned">
                    <td className="px-3 py-1.5 italic text-zinc-500">Non alloué (congés / maladie / formation / …)</td>
                    <td className="px-3 py-1.5 font-mono text-zinc-500">MAD</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtMad(byMission.unassigned_mad)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-600">
                      {byMission.total_mad > 0 ? (byMission.unassigned_mad / byMission.total_mad * 100).toFixed(1) : "0.0"}%
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-zinc-200 bg-zinc-50/60 font-semibold">
                  <td className="px-3 py-1.5" colSpan={2}>TOTAL</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtMad(byMission.total_mad)}</td>
                  <td className="px-3 py-1.5 text-right font-mono">100.0%</td>
                </tr>
              </tfoot>
            </table>
            </div>
          )}
          {byMission.reconciles_with_monthly_statement === false && (
            <div className="px-4 py-2 text-[10px] text-amber-700 bg-amber-50 border-t border-amber-200 font-mono">
              ⚠ Rapprochement avec l&apos;état mensuel non atteint — vérifiez la cohérence CRA/rémunération.
            </div>
          )}
        </div>
      )}

      {/* Iter-228y — Auto-email settings dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl" data-testid="real-margin-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={18} /> Rapport P&amp;L mensuel automatique</DialogTitle>
            <DialogDescription>
              Envoie automatiquement le rapport P&amp;L (marge nette réelle) par email le 1er de chaque mois à 08:00 UTC, pour le mois précédent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={emailForm.real_margin_email_enabled}
                onChange={(e) => setEmailForm({ ...emailForm, real_margin_email_enabled: e.target.checked })}
                data-testid="real-margin-settings-enabled"
                className="rounded border-zinc-300"
              />
              <span className="text-sm">Activer l&apos;envoi automatique mensuel</span>
            </label>
            <div>
              <label className="text-xs uppercase tracking-wider font-mono text-zinc-500 block mb-1">Destinataires (To) — séparés par virgule</label>
              <input
                type="text"
                value={emailForm.real_margin_email_to}
                onChange={(e) => setEmailForm({ ...emailForm, real_margin_email_to: e.target.value })}
                data-testid="real-margin-settings-to"
                className="w-full border border-zinc-300 rounded px-2 py-1.5 text-sm"
                placeholder="ceo@ba-inmotion.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-mono text-zinc-500 block mb-1">Copie carbone (Cc)</label>
              <input
                type="text"
                value={emailForm.real_margin_email_cc}
                onChange={(e) => setEmailForm({ ...emailForm, real_margin_email_cc: e.target.value })}
                data-testid="real-margin-settings-cc"
                className="w-full border border-zinc-300 rounded px-2 py-1.5 text-sm"
                placeholder="gestionnaire@ba-inmotion.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-mono text-zinc-500 block mb-1">Sujet (variables : {`{{period}}`})</label>
              <input
                type="text"
                value={emailForm.real_margin_email_subject_template}
                onChange={(e) => setEmailForm({ ...emailForm, real_margin_email_subject_template: e.target.value })}
                data-testid="real-margin-settings-subject"
                className="w-full border border-zinc-300 rounded px-2 py-1.5 text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-mono text-zinc-500 block mb-1">
                Corps (variables : {`{{period}} {{revenue}} {{expenses}} {{salaries}} {{net}} {{margin_pct}}`})
              </label>
              <textarea
                rows={7}
                value={emailForm.real_margin_email_body_template}
                onChange={(e) => setEmailForm({ ...emailForm, real_margin_email_body_template: e.target.value })}
                data-testid="real-margin-settings-body"
                className="w-full border border-zinc-300 rounded px-2 py-1.5 text-sm font-mono"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button" onClick={() => setShowSettings(false)}
                className="px-3 py-1.5 text-sm border border-zinc-300 rounded bg-white hover:bg-zinc-50"
              >
                Annuler
              </button>
              <button
                type="button" onClick={async () => { await saveEmailCfg(); setShowSettings(false); }}
                data-testid="real-margin-settings-save-btn"
                className="px-3 py-1.5 text-sm bg-zinc-900 text-white rounded hover:bg-zinc-800"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
